import React from 'react';
import { Check, CheckCheck, Clock, FileText, Paperclip } from 'lucide-react';
import { instanceLabel } from '../../../lib/whatsappInstances';
import { AudioPlayer } from './AudioPlayer';
import type { ChatMessage } from './mergeMessages';

const PLACEHOLDERS = ['[imagem]', '[áudio]', '[audio]', '[vídeo]', '[video]', '[documento]'];
const isPlaceholder = (body: string) => PLACEHOLDERS.includes(body.trim().toLowerCase());

// Mensagens do CRM vêm como "*Nome*:\ntexto" (assinatura do atendente).
function splitSignature(body: string): { name: string | null; text: string } {
    const match = body.match(/^\*([^*]+)\*:\n(.*)/s);
    return match ? { name: match[1], text: match[2] } : { name: null, text: body };
}

// Status de entrega no padrão do WhatsApp: relógio (enviando), ✓ enviada,
// ✓✓ cinza entregue, ✓✓ azul lida. O relógio só aparece enquanto a mensagem está sendo enviada.
function DeliveryStatus({ message }: { message: ChatMessage }) {
    if (message.isPending) {
        return <Clock size={12} className="opacity-70" aria-label="Enviando" />;
    }
    switch (message.status) {
        case 'READ':
            return <CheckCheck size={14} className="text-sky-200" aria-label="Lida" />;
        case 'DELIVERED':
            return <CheckCheck size={14} className="opacity-70" aria-label="Entregue" />;
        case 'FAILED':
            return <span className="text-red-300 font-medium">Falha</span>;
        default:
            return <Check size={14} className="opacity-70" aria-label="Enviada" />;
    }
}

interface MessageBubbleProps {
    message: ChatMessage;
}

// Memoizado: o chat pode ter centenas de mensagens e só re-renderiza as que mudaram
// (a identidade do objeto é preservada quando nada mudou — ver mergeMessages).
export const MessageBubble = React.memo(function MessageBubble({ message: item }: MessageBubbleProps) {
    const isOutbound = item.direction === 'OUTBOUND';
    const body = item.message_body || '';
    const bodyIsPlaceholder = isPlaceholder(body);
    const showText = !!body && (!item.media_url || !bodyIsPlaceholder);
    const { name, text } = splitSignature(body);

    return (
        <div className={`flex ${isOutbound ? 'justify-end' : 'justify-start'} group`}>
            <div className={`max-w-[75%] rounded-2xl px-4 py-2 shadow-sm relative ${
                isOutbound
                    ? 'bg-gradient-to-br from-green-500 to-green-600 text-white rounded-br-sm'
                    : 'bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 rounded-bl-sm border border-slate-100 dark:border-slate-700'
            }`}>
                {item.message_type === 'image' && item.media_url && (
                    <div className="mb-2 mt-1 -mx-2">
                        <a href={item.media_url} target="_blank" rel="noreferrer">
                            <img
                                src={item.media_url}
                                alt="Mídia"
                                loading="lazy"
                                decoding="async"
                                className="w-full rounded-xl max-h-64 object-cover cursor-pointer hover:opacity-90 transition-opacity shadow-sm"
                            />
                        </a>
                    </div>
                )}
                {item.message_type === 'audio' && item.media_url && (
                    <AudioPlayer src={item.media_url} isOutbound={isOutbound} />
                )}
                {item.message_type === 'video' && item.media_url && (
                    <div className="mb-2 mt-1 -mx-2">
                        <video controls preload="none" src={item.media_url} className="w-full rounded-xl max-h-64 shadow-sm" />
                    </div>
                )}
                {item.message_type === 'document' && item.media_url && (
                    <div className={`mb-2 mt-1 p-3 rounded-xl flex items-center gap-3 ${isOutbound ? 'bg-green-700/30' : 'bg-slate-100 dark:bg-slate-700/50'}`}>
                        <div className={`p-2 rounded-lg ${isOutbound ? 'bg-white/20' : 'bg-white dark:bg-slate-600'}`}>
                            <FileText size={20} className={isOutbound ? 'text-white' : 'text-blue-500'} />
                        </div>
                        <div className="flex-1 overflow-hidden">
                            <a href={item.media_url} target="_blank" rel="noreferrer" className="text-sm font-medium hover:underline truncate block">
                                Baixar Documento
                            </a>
                        </div>
                    </div>
                )}

                {showText && (
                    <div className="whitespace-pre-wrap text-[15px] leading-relaxed">
                        {!item.media_url && bodyIsPlaceholder && (
                            <span className="text-xs opacity-75 flex items-center gap-1 mb-1"><Paperclip size={12} /> Mídia não sincronizada</span>
                        )}
                        {name ? (
                            <div className="flex flex-col">
                                <span className={`text-[11px] font-bold uppercase tracking-wider mb-0.5 opacity-80 ${isOutbound ? 'text-green-100' : 'text-slate-500 dark:text-slate-400'}`}>
                                    {name}
                                </span>
                                <span>{text}</span>
                            </div>
                        ) : (
                            <span>{body}</span>
                        )}
                    </div>
                )}

                <div className={`text-[11px] flex items-center gap-1.5 mt-1.5 ${isOutbound ? 'text-green-100' : 'text-slate-500 dark:text-slate-400'}`}>
                    {new Date(item.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    {item.whatsapp_instances && (
                        <>
                            <span className="opacity-50">•</span>
                            <span className="opacity-90">via {instanceLabel(item.whatsapp_instances)}</span>
                        </>
                    )}
                    {isOutbound && (
                        <span className="ml-1">
                            <DeliveryStatus message={item} />
                        </span>
                    )}
                </div>
            </div>
        </div>
    );
});
